/**
 * PDF + bbox overlay viewer — aligned with SAS packages/web PDFViewer.
 * Critical: pass a URL string (or { url, httpHeaders }) to <Document>, NEVER { data: ArrayBuffer }.
 * Passing ArrayBuffer/Uint8Array causes "ArrayBuffer is detached" when pdf.js transfers it to the worker.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Serve worker from /public (copied from pdfjs-dist). Query busts stale caches.
pdfjs.GlobalWorkerOptions.workerSrc = `${import.meta.env.BASE_URL}pdf.worker.min.mjs?v=${pdfjs.version}`;

const PDF_OPTIONS = {
  // Auth-proxied /api PDF does not implement byte-range; single full GET with Bearer (SAS uses URL, not ArrayBuffer).
  disableRange: true,
  disableStream: true,
  isEvalSupported: false,
};

export type PdfContentItem = {
  text?: string;
  type?: string;
  bbox?: [number, number, number, number];
  page_idx?: number;
  img_path?: string;
  _originalIndex?: number;
  _globalIndex?: number;
  [key: string]: unknown;
};

export type PdfSplitLine = {
  page_idx: number;
  y_norm: number;
  label?: string;
};

type Props = {
  url: string;
  /** Bearer token for AppFactory PDF proxy (react-pdf httpHeaders). */
  authToken?: string | null;
  items: PdfContentItem[];
  /** Horizontal seam lines (y_norm 0=top … 1=bottom) for semantic_split. */
  splitLines?: PdfSplitLine[];
  selectedItemIndex?: number;
  /** Prefer scrolling to this 0-based PDF page (e.g. extract unit pageSpan) when no bbox anchors. */
  scrollToPageIdx?: number | null;
  /**
   * Increment to request a one-shot PDF scroll (list/bbox click).
   * Do not keep auto-scrolling on every items/poll re-render — that fights free scroll.
   */
  scrollRequestId?: number;
  highlightedItemIndices?: number[];
  onItemClick?: (index: number) => void;
  /** Most-visible PDF page changed by user scroll (0-based). */
  onActivePageChange?: (pageIdx: number) => void;
  hiddenTypes?: string[];
  autoScrollOnSelect?: boolean;
  layoutMode?: 'fit-width' | 'fit-height';
  className?: string;
  /** After load, pin scroll to start (next booklet) or end (prev booklet). */
  scrollAnchor?: 'start' | 'end';
  /** Fired when user keeps scrolling past the top/bottom of this booklet. */
  onEdgeNavigate?: (direction: 'prev' | 'next') => void;
};

export function PDFViewer({
  url,
  authToken,
  items,
  splitLines = [],
  selectedItemIndex,
  scrollToPageIdx = null,
  scrollRequestId = 0,
  highlightedItemIndices,
  onItemClick,
  onActivePageChange,
  hiddenTypes = ['header', 'footer', 'page_number'],
  autoScrollOnSelect = true,
  layoutMode = 'fit-width',
  className = '',
  scrollAnchor = 'start',
  onEdgeNavigate,
}: Props) {
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(520);
  const [pageHeight, setPageHeight] = useState(720);
  const [loadError, setLoadError] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set([0, 1, 2]));
  const observerRef = useRef<IntersectionObserver | null>(null);
  const edgeCooldownRef = useRef(false);
  const edgeAccumRef = useRef(0);
  const onEdgeNavigateRef = useRef(onEdgeNavigate);
  onEdgeNavigateRef.current = onEdgeNavigate;
  const onActivePageChangeRef = useRef(onActivePageChange);
  onActivePageChangeRef.current = onActivePageChange;
  const lastReportedPageRef = useRef<number | null>(null);
  const lastScrollRequestRef = useRef<number>(-1);
  const suppressPageReportUntilRef = useRef(0);
  const pageRatioRef = useRef<Map<number, number>>(new Map());
  const hiddenTypeSet = useMemo(
    () => new Set(hiddenTypes.map((t) => t.toLowerCase())),
    [hiddenTypes],
  );

  // Document receives URL (+ auth headers). Never pass ArrayBuffer / Uint8Array.
  const file = useMemo(() => {
    const token = String(authToken || '').trim();
    if (!token) return url;
    return {
      url,
      httpHeaders: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, [url, authToken]);

  const splitLinesByPage = useMemo(() => {
    const map = new Map<number, PdfSplitLine[]>();
    for (const line of splitLines) {
      const p = Number(line.page_idx ?? 0);
      const list = map.get(p) || [];
      list.push(line);
      map.set(p, list);
    }
    return map;
  }, [splitLines]);

  const itemsByPage = useMemo(() => {
    const map = new Map<number, PdfContentItem[]>();
    items.forEach((item, index) => {
      const p = Number(item.page_idx ?? 0);
      if (!map.has(p)) map.set(p, []);
      map.get(p)!.push({ ...item, _originalIndex: item._originalIndex ?? index });
    });
    return map;
  }, [items]);

  useEffect(() => {
    setLoadError('');
    setNumPages(0);
    setVisiblePages(new Set([0, 1, 2]));
    edgeAccumRef.current = 0;
    edgeCooldownRef.current = false;
  }, [url]);

  // Snap scroll after booklet switch only (not while user freely scrolls).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !numPages) return;
    if (scrollAnchor === 'end') {
      setVisiblePages((prev) => {
        const next = new Set(prev);
        for (let i = Math.max(0, numPages - 3); i < numPages; i++) next.add(i);
        return next;
      });
      const snapEnd = () => {
        el.scrollTop = el.scrollHeight;
      };
      snapEnd();
      const raf = requestAnimationFrame(snapEnd);
      const t1 = window.setTimeout(snapEnd, 120);
      const t2 = window.setTimeout(snapEnd, 400);
      return () => {
        cancelAnimationFrame(raf);
        window.clearTimeout(t1);
        window.clearTimeout(t2);
      };
    }
    el.scrollTop = 0;
  }, [url, scrollAnchor, numPages]);

  // At booklet edge, further wheel → prev/next booklet (parent shows toast if none).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const EDGE_PX = 4;
    const THRESHOLD = 90;

    const atTop = () => el.scrollTop <= EDGE_PX;
    const atBottom = () => el.scrollTop + el.clientHeight >= el.scrollHeight - EDGE_PX;

    const fire = (direction: 'prev' | 'next') => {
      if (edgeCooldownRef.current) return;
      if (!onEdgeNavigateRef.current) return;
      edgeCooldownRef.current = true;
      edgeAccumRef.current = 0;
      onEdgeNavigateRef.current(direction);
      window.setTimeout(() => {
        edgeCooldownRef.current = false;
      }, 900);
    };

    const onWheel = (e: WheelEvent) => {
      if (edgeCooldownRef.current) return;
      if (e.deltaY > 0 && atBottom()) {
        edgeAccumRef.current += e.deltaY;
        if (edgeAccumRef.current >= THRESHOLD) fire('next');
      } else if (e.deltaY < 0 && atTop()) {
        edgeAccumRef.current += -e.deltaY;
        if (edgeAccumRef.current >= THRESHOLD) fire('prev');
      } else {
        edgeAccumRef.current = 0;
      }
    };

    el.addEventListener('wheel', onWheel, { passive: true });
    return () => el.removeEventListener('wheel', onWheel);
  }, [url]);

  useEffect(() => {
    const updateSize = () => {
      if (!containerRef.current) return;
      // Cap sizes: if the scroll container is height:auto, clientHeight grows with
      // content and fit-height pages explode (browser max ~16_777_216px).
      const rawW = containerRef.current.clientWidth - 24;
      const rawH = containerRef.current.clientHeight - 24;
      const nextW = Math.min(1400, Math.max(240, rawW));
      // Prefer viewport-bounded height when the container has already ballooned.
      const viewportCap = Math.round(window.innerHeight * 0.72);
      const nextH = Math.min(
        1200,
        Math.max(320, rawH > 2000 ? viewportCap : rawH),
      );
      setPageWidth(nextW);
      setPageHeight(nextH);
    };
    const observer = new ResizeObserver(updateSize);
    if (containerRef.current) observer.observe(containerRef.current);
    updateSize();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const reportBestPage = () => {
      if (Date.now() < suppressPageReportUntilRef.current) return;
      let bestPage = -1;
      let bestRatio = 0;
      for (const [pageIdx, ratio] of pageRatioRef.current) {
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestPage = pageIdx;
        }
      }
      if (bestPage < 0 || bestRatio < 0.12) return;
      if (lastReportedPageRef.current === bestPage) return;
      lastReportedPageRef.current = bestPage;
      onActivePageChangeRef.current?.(bestPage);
    };

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const pageIdx = Number(entry.target.getAttribute('data-page-idx') || 0);
          pageRatioRef.current.set(pageIdx, entry.isIntersecting ? entry.intersectionRatio : 0);
          setVisiblePages((prev) => {
            const next = new Set(prev);
            if (entry.isIntersecting) {
              next.add(pageIdx);
              if (pageIdx > 0) next.add(pageIdx - 1);
              if (pageIdx < numPages - 1) next.add(pageIdx + 1);
            }
            const visible = [...next].filter((p) => p >= 0 && p < numPages);
            if (visible.length) {
              const minV = Math.min(...visible);
              const maxV = Math.max(...visible);
              for (const p of next) {
                if (p < minV - 3 || p > maxV + 3) next.delete(p);
              }
            }
            return next;
          });
        });
        reportBestPage();
      },
      { root: containerRef.current, rootMargin: '80px 0px', threshold: [0.01, 0.25, 0.5, 0.75] },
    );
    for (const [, el] of pageRefs.current) observerRef.current.observe(el);
    return () => observerRef.current?.disconnect();
  }, [numPages]);

  const observePage = useCallback((el: HTMLDivElement | null, pageIdx: number) => {
    if (el) {
      pageRefs.current.set(pageIdx, el);
      observerRef.current?.observe(el);
    } else {
      const existing = pageRefs.current.get(pageIdx);
      if (existing) observerRef.current?.unobserve(existing);
      pageRefs.current.delete(pageIdx);
      pageRatioRef.current.delete(pageIdx);
    }
  }, []);

  // One-shot scroll only when parent bumps scrollRequestId (list/bbox click).
  useEffect(() => {
    if (!autoScrollOnSelect) return;
    if (!scrollRequestId || scrollRequestId === lastScrollRequestRef.current) return;
    lastScrollRequestRef.current = scrollRequestId;

    let pageIdx: number | null = null;
    let selectedIndexForBbox: number | null = null;

    if (selectedItemIndex != null && selectedItemIndex >= 0) {
      const targetItem =
        items.find((i) => i._originalIndex === selectedItemIndex) ?? items[selectedItemIndex];
      if (targetItem) {
        pageIdx = Number(targetItem.page_idx ?? 0);
        selectedIndexForBbox = selectedItemIndex;
      }
    }
    if (
      (pageIdx == null || !Number.isFinite(pageIdx))
      && scrollToPageIdx != null
      && Number.isFinite(scrollToPageIdx)
      && scrollToPageIdx >= 0
    ) {
      pageIdx = Math.floor(Number(scrollToPageIdx));
    }
    if (pageIdx == null || !Number.isFinite(pageIdx) || pageIdx < 0) return;

    const targetPage = pageIdx;
    suppressPageReportUntilRef.current = Date.now() + 700;
    lastReportedPageRef.current = targetPage;
    setVisiblePages((prev) => {
      const next = new Set(prev);
      next.add(targetPage);
      if (targetPage > 0) next.add(targetPage - 1);
      if (numPages > 0 && targetPage < numPages - 1) next.add(targetPage + 1);
      return next;
    });

    let cancelled = false;
    const tryScroll = (attempt = 0) => {
      if (cancelled) return;
      if (selectedIndexForBbox != null) {
        const target = containerRef.current?.querySelector(
          `[data-item-index="${selectedIndexForBbox}"][data-page-idx="${targetPage}"]`,
        ) as HTMLElement | null;
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
      const pageEl = pageRefs.current.get(targetPage);
      if (pageEl) {
        pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (attempt < 25) window.setTimeout(() => tryScroll(attempt + 1), 120);
    };
    const t = window.setTimeout(() => tryScroll(0), 40);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [
    scrollRequestId,
    selectedItemIndex,
    scrollToPageIdx,
    items,
    autoScrollOnSelect,
    numPages,
  ]);

  const estimatedPageHeight =
    layoutMode === 'fit-height'
      ? pageHeight > 100
        ? pageHeight
        : 600
      : Math.round((pageWidth > 200 ? pageWidth : 520) * 1.414);

  return (
    <div className={`pdf-viewer ${className}`.trim()} ref={containerRef}>
      {loadError ? <div className="error">PDF 加载失败（{loadError}）</div> : null}
      <Document
        key={typeof file === 'string' ? file : file.url}
        file={file}
        options={PDF_OPTIONS}
        onLoadSuccess={(pdf) => {
          setLoadError('');
          setNumPages(pdf.numPages);
          setVisiblePages((prev) => {
            const next = new Set(prev);
            next.add(0);
            next.add(1);
            next.add(2);
            return next;
          });
        }}
        onLoadError={(err) => {
          setLoadError(err instanceof Error ? err.message : String(err));
        }}
        loading={<div className="empty">加载 PDF…</div>}
        error={<div className="error">PDF 加载失败{loadError ? `（${loadError}）` : ''}</div>}
        className="pdf-doc"
      >
        {Array.from({ length: numPages }, (_, index) => (
          <div
            key={`page-${index}`}
            data-page-idx={index}
            className="pdf-page-wrap"
            ref={(el) => observePage(el, index)}
            style={{
              minHeight: visiblePages.has(index) ? undefined : estimatedPageHeight,
            }}
          >
            {visiblePages.has(index) ? (
              <PDFPageWithOverlay
                pageNumber={index + 1}
                pageIdx={index}
                width={layoutMode === 'fit-height' ? undefined : Math.max(240, pageWidth)}
                height={layoutMode === 'fit-height' ? Math.max(320, pageHeight) : undefined}
                items={itemsByPage.get(index) || []}
                splitLines={splitLinesByPage.get(index) || []}
                onItemClick={onItemClick}
                selectedIndex={selectedItemIndex}
                highlightedIndices={highlightedItemIndices}
                hiddenTypeSet={hiddenTypeSet}
              />
            ) : (
              <div className="pdf-page-placeholder" style={{ height: estimatedPageHeight }}>
                第 {index + 1} 页
              </div>
            )}
          </div>
        ))}
      </Document>
    </div>
  );
}

function PDFPageWithOverlay({
  pageNumber,
  pageIdx,
  width,
  height,
  items,
  splitLines,
  onItemClick,
  selectedIndex,
  highlightedIndices,
  hiddenTypeSet,
}: {
  pageNumber: number;
  pageIdx: number;
  width?: number;
  height?: number;
  items: PdfContentItem[];
  splitLines: PdfSplitLine[];
  onItemClick?: (index: number) => void;
  selectedIndex?: number;
  highlightedIndices?: number[];
  hiddenTypeSet: Set<string>;
}) {
  const [meta, setMeta] = useState<{ width: number; height: number; view: number[] } | null>(null);

  return (
    <div className="pdf-page-relative">
      <Page
        pageNumber={pageNumber}
        width={width}
        height={height}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        onLoadSuccess={(page) => {
          const viewport = page.getViewport({ scale: 1 });
          setMeta({
            view: page.view as number[],
            width: viewport.width,
            height: viewport.height,
          });
        }}
      >
        {meta
          ? (() => {
              const scale = width
                ? width / meta.width
                : height
                  ? height / meta.height
                  : 1;
              const renderedW = width ?? meta.width * scale;
              const renderedH = height ?? meta.height * scale;
              const validBoxes = items
                .map((i) => i.bbox)
                .filter((b): b is [number, number, number, number] => Array.isArray(b) && b.length >= 4);
              const maxX = validBoxes.length ? Math.max(...validBoxes.map((b) => Number(b[2]) || 0)) : 0;
              const maxY = validBoxes.length ? Math.max(...validBoxes.map((b) => Number(b[3]) || 0)) : 0;
              const isNormalized = maxX <= 1.5 && maxY <= 1.5;
              const isPdfPoints =
                !isNormalized && maxX <= meta.width * 1.1 && maxY <= meta.height * 1.1;
              const is1000Based = !isNormalized && !isPdfPoints && maxX <= 1000 && maxY <= 1000;

              const projectBox = (
                bbox: [number, number, number, number],
                yMode: 'top-left' | 'bottom-left',
              ) => {
                const [x0, y0, x1, y1] = bbox;
                let rectX = 0;
                let rectY = 0;
                let rectW = 0;
                let rectH = 0;
                if (isNormalized) {
                  rectX = x0 * renderedW;
                  rectW = (x1 - x0) * renderedW;
                  rectH = (y1 - y0) * renderedH;
                  rectY = yMode === 'top-left' ? y0 * renderedH : renderedH - y1 * renderedH;
                } else if (isPdfPoints) {
                  const inCrop =
                    x0 >= 0 && y0 >= 0 && x1 <= meta.width && y1 <= meta.height;
                  const offsetX = inCrop ? 0 : meta.view[0] || 0;
                  const offsetY = inCrop ? 0 : meta.view[1] || 0;
                  const py0 = y0 - offsetY;
                  const py1 = y1 - offsetY;
                  rectX = (x0 - offsetX) * scale;
                  rectW = (x1 - x0) * scale;
                  rectH = (py1 - py0) * scale;
                  rectY = yMode === 'top-left' ? py0 * scale : renderedH - py1 * scale;
                } else if (is1000Based) {
                  rectX = (x0 / 1000) * renderedW;
                  rectW = ((x1 - x0) / 1000) * renderedW;
                  rectH = ((y1 - y0) / 1000) * renderedH;
                  rectY =
                    yMode === 'top-left' ? (y0 / 1000) * renderedH : renderedH - (y1 / 1000) * renderedH;
                } else {
                  const sourceWidth = Math.max(maxX, 1);
                  const pageAspect = meta.width / meta.height;
                  const sourceHeight = Math.max(maxY, sourceWidth / pageAspect, 1) * 1.03;
                  const scaleX = renderedW / sourceWidth;
                  const scaleY = renderedH / sourceHeight;
                  rectX = x0 * scaleX;
                  rectW = (x1 - x0) * scaleX;
                  rectH = (y1 - y0) * scaleY;
                  rectY = yMode === 'top-left' ? y0 * scaleY : renderedH - y1 * scaleY;
                }
                return {
                  x: Math.max(0, rectX),
                  y: Math.max(0, rectY),
                  w: Math.max(1, rectW),
                  h: Math.max(1, rectH),
                };
              };

              const boxScore = (r: { x: number; y: number; w: number; h: number }) => {
                const insideX = r.x >= -8 && r.x + r.w <= renderedW + 8;
                const insideY = r.y >= -8 && r.y + r.h <= renderedH + 8;
                return (insideX ? 1 : 0) + (insideY ? 1 : 0);
              };
              let topScore = 0;
              let bottomScore = 0;
              for (const bbox of validBoxes.slice(0, 80)) {
                topScore += boxScore(projectBox(bbox, 'top-left'));
                bottomScore += boxScore(projectBox(bbox, 'bottom-left'));
              }
              const yMode: 'top-left' | 'bottom-left' =
                bottomScore > topScore ? 'bottom-left' : 'top-left';

              const boxes = items.map((item, idx) => {
                const itemType = String(item.type || '').toLowerCase();
                if (hiddenTypeSet.has(itemType)) return null;
                const bbox = item.bbox;
                if (!bbox || bbox.length < 4) return null;
                const rect = projectBox(bbox, yMode);
                const isSelected = selectedIndex === item._originalIndex;
                const isHighlighted = highlightedIndices?.includes(Number(item._originalIndex));
                return (
                  <div
                    key={`b-${idx}`}
                    data-item-index={item._originalIndex}
                    data-page-idx={pageIdx}
                    className={`pdf-bbox${isSelected ? ' selected' : ''}${isHighlighted && !isSelected ? ' highlighted' : ''}`}
                    style={{
                      left: rect.x,
                      top: rect.y,
                      width: rect.w,
                      height: rect.h,
                    }}
                    title={item.text}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item._originalIndex != null) onItemClick?.(item._originalIndex);
                    }}
                  />
                );
              });
              const seams = splitLines.map((line, li) => {
                const y = Math.min(1, Math.max(0, Number(line.y_norm) || 0));
                return (
                  <div
                    key={`s-${li}`}
                    className="pdf-split-line"
                    title={line.label || `y=${(y * 100).toFixed(1)}%`}
                    style={{
                      position: 'absolute',
                      left: 0,
                      width: renderedW,
                      top: y * renderedH,
                      borderTop: '2px solid #d9480f',
                      boxShadow: '0 0 0 1px rgba(255,255,255,0.65)',
                      pointerEvents: 'none',
                      zIndex: 5,
                    }}
                  />
                );
              });
              return (
                <>
                  {boxes}
                  {seams}
                </>
              );
            })()
          : null}
      </Page>
    </div>
  );
}
